import React, { useMemo } from "react";

import { isNil } from "lodash";
import PropTypes from "prop-types";
import { Trans, useTranslation } from "react-i18next";
import * as yup from "yup";

import ErrorBox from "@shared/components/errorBox/ErrorBox";
import Popup from "@shared/components/popup/Popup";
import websheetUtilities from "@shared/helpers/websheetUtilities";

import { useDataTable } from "@app/hooks/useDataTable";

import { Text } from "@atoms/Text";

import Form from "@components/atoms/Form/Form";
import ModalForm from "@components/molecules/ModalForm";

import PreviewModalTables from "./PreviewModalTables";

const MergeColumnsModal = props => {
  const {
    mergeColumnIndex,
    columns,
    handleCancel,
    afterData,
    beforeData,
    onDelimiterChange,
    onColumnSelectionChange,
    handleSubmit,
    errorMessage
  } = props;
  const { createColumn } = useDataTable();
  const { t } = useTranslation();

  const beforeColumns = useMemo(() => {
    return [
      createColumn({
        accessor: "beforeLeft",
        width: 160,
        disableSortBy: true
      }),
      createColumn({
        accessor: "beforeRight",
        width: 160,
        disableSortBy: true
      })
    ];
  }, [createColumn]);

  const afterColumns = useMemo(() => {
    if (isNil(afterData[0]?.after)) {
      return beforeColumns;
    }
    return [
      createColumn({
        accessor: "after",
        width: 320,
        disableSortBy: true
      })
    ];
  }, [afterData, beforeColumns, createColumn]);

  if (!columns?.length || mergeColumnIndex === null) {
    return <></>;
  }

  const columnItems = columns
    .map((_, idx) => ({
      name: `Column ${websheetUtilities.convertNumToColumn(idx + 1)}`,
      value: idx
    }))
    .filter((_, idx) => idx !== mergeColumnIndex);

  const getDescription = () => {
    return (
      <Trans
        i18nKey="common:ui.websheet.actions.cleaningWizard.step.mergeColumns.description"
        values={{
          columnName: websheetUtilities.convertNumToColumn(mergeColumnIndex + 1)
        }}
        shouldUnescape={true}
        components={{ bold: <strong /> }}
      />
    );
  };

  return (
    <Popup visibility={true} width="50rem">
      <ModalForm
        title={t(
          "common:ui.websheet.actions.cleaningWizard.step.mergeColumns.title"
        )}
        submitLabel={t("common:ui.forms.apply.label")}
        cancelLabel={t("common:ui.forms.cancel.label")}
        handleCancel={handleCancel}
        yupSchema={yup.object({
          column: yup.object().required(),
          delimiter: yup.string().optional()
        })}
        handleSubmit={handleSubmit}
        boxClassName={"merge-columns-modal"}
      >
        {errorMessage && <ErrorBox message={errorMessage} />}
        <Text text={getDescription()} />
        <Form.Dropdown
          name={"column"}
          label={t(
            "common:ui.websheet.actions.cleaningWizard.step.mergeColumns.column.label"
          )}
          placeholder={t(
            "common:ui.websheet.actions.cleaningWizard.step.mergeColumns.column.placeholder"
          )}
          items={columnItems}
          required={true}
          onChange={onColumnSelectionChange}
        />
        <Form.TextField
          name={"delimiter"}
          label={t(
            "common:ui.websheet.actions.cleaningWizard.step.mergeColumns.delimiter.label"
          )}
          placeholder={t(
            "common:ui.websheet.actions.cleaningWizard.step.delimiter.placeholder"
          )}
          required={false}
          onChange={onDelimiterChange}
        />
        <PreviewModalTables
          afterData={afterData}
          afterColumns={afterColumns}
          beforeData={beforeData}
          beforeColumns={beforeColumns}
        />
      </ModalForm>
    </Popup>
  );
};

MergeColumnsModal.propTypes = {
  mergeColumnIndex: PropTypes.number,
  columns: PropTypes.array,
  handleCancel: PropTypes.func,
  afterData: PropTypes.array,
  beforeData: PropTypes.array,
  onDelimiterChange: PropTypes.func,
  onColumnSelectionChange: PropTypes.func,
  handleSubmit: PropTypes.func,
  errorMessage: PropTypes.string
};

export default MergeColumnsModal;
